import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { mapPlaylistFromVideoSearch, DataTable, columnTypes as colType, innings, percent } from 'yankees-react-utils';
import { store } from '../../../../store';

const StyledSuperHeaderRow = styled.tr`
    background-color: #C6D3DC;
`;
const StyledStrong = styled.strong`
    display: flex;
    justify-content: flex-start;
`;
const StyledSuperHeaderCell = styled.th`
    && {
        border-left: 1px solid #244C74;
        border-right: 1px solid #244C74;
        background-color: #244C74 !important;
        color: #FFF;
    }
`;

const getCategoryVideo = (user, pitchLog = [], pitchtype = null, category = null, length = null, adv = false) => {
    let results = adv ? pitchLog.filter((p) => p.advr) : pitchLog;

    if (pitchtype) {
        switch (pitchtype) {
        case 'fb': results = results.filter((p) => [1, 9, 10].indexOf(p.pitchtype) > -1); break;
        case 'cb': results = results.filter((p) => p.pitchtype === 2); break;
        case 'slcut': results = results.filter((p) => [3, 8].indexOf(p.pitchtype) > -1); break;
        case 'chspl': results = results.filter((p) => [4, 7].indexOf(p.pitchtype) > -1); break;
        default: break;
        }
    }

    if (category) {
        switch (category) {
        case 'bcs': results = results.filter((p) => p.bcs === 1); break;
        case 'rhh_in': results = results.filter((p) => p.rhh_in === 1); break;
        case 'rhh_away': results = results.filter((p) => p.rhh_away === 1); break;
        case 'lhh_in': results = results.filter((p) => p.lhh_in === 1); break;
        case 'lhh_away': results = results.filter((p) => p.lhh_away === 1); break;
        case 'middle': results = results.filter((p) => p.middle === 1); break;
        case 'r1': results = results.filter((p) => p.r1 === 1); break;
        case 'r2': results = results.filter((p) => p.r2 === 1); break;
        case 'r3': results = results.filter((p) => p.r3 === 1); break;
        case 'r13': results = results.filter((p) => p.r13 === 1); break;
        case 'twok': results = results.filter((p) => p.twok === 1); break;
        case 'advr': results = results.filter((p) => p.advr === 1); break;
        case 'gloveside': results = results.filter((p) => p.gloveside === 1); break;
        case 'armside': results = results.filter((p) => p.armside === 1); break;
        default: break;
        }
    }

    if (length) {
        switch (length) {
        case 'long': results = results.filter((p) => p.long === 1); break;
        case 'short': results = results.filter((p) => p.short === 1); break;
        default: break;
        }
    }

    return mapPlaylistFromVideoSearch({ videos: results, playervideos: [] }, user.Roles.map((r) => r.RoleName));
};

const buildPercentCell = (value, pitches, precision = 1, includeSign = true, setPlaylistFn = null) => {
    let sign = '';
    if (value && (value * 100).toFixed(precision) > 0) {
        sign = '+';
    } else if (value && (value * 100).toFixed(precision) < 0) {
        sign = '-';
    }
    if (setPlaylistFn) {
        return (
            <div>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#" onClick={setPlaylistFn}>
                    {includeSign ? `${sign}${percent(value, precision)}` : percent(value, precision)}
                </a>
                <br />
                <span style={{ fontSize: '0.85em' }}>{`(${+(pitches || 0).toFixed(precision)})`}</span>
            </div>
        );
    }
    return (
        <div>
            <span>
                {includeSign ? `${sign}${percent(value, precision)}` : percent(value, precision)}
                <span style={{ fontSize: '0.85em' }}><br />{`(${+(pitches || 0).toFixed(precision)})`}</span>
            </span>
        </div>
    );
};

const BlockingReportTable = ({ tableType, rows, superheaderName, pitches, onClick }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const pitchTypes = ['fb', 'cb', 'slcut', 'chspl'];
    const getColumns = (type) => {
        let columns = [];
        if (type === 'overall') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                { id: 'totalgames', title: translate('sections.reports.catcher.blocking.total-games'), transform: (row) => row.g || '--' },
                { id: 'totalinnings', title: translate('sections.reports.catcher.blocking.total-innings'), transform: (row) => innings(row.outs) || '--', precision: 1 },
                { id: 'pitchesindirt', title: translate('sections.reports.catcher.blocking.pitches-in-dirt'), transform: (row) => row.pitchesindirt || '--' },
            ];
        } else if (type === 'total') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                { id: 'br120', title: translate('sections.reports.catcher.blocking.abbr-br120'), transform: (row) => row.wppbrv, precision: 1 },
                {
                    id: 'adv%',
                    title: translate('sections.reports.catcher.blocking.abbr-advpct'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, null, null, true));
                        return buildPercentCell((row.adv / row.pitchesindirt), row.pitchesindirt, 1, false, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
            ];
        } else if (type === 'bouncelocation') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                {
                    id: 'glove',
                    title: translate('sections.reports.catcher.blocking.glove-side'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'gloveside'));
                        return buildPercentCell(row.glovesidewppm, row.gloveside, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'middle',
                    title: translate('sections.reports.catcher.blocking.middle'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'middle'));
                        return buildPercentCell(row.middlewppm, row.middle, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'arm',
                    title: translate('sections.reports.catcher.blocking.arm-side'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'armside'));
                        return buildPercentCell(row.armsidewppm, row.armside, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'short',
                    title: translate('sections.reports.catcher.blocking.short'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'short'));
                        return buildPercentCell(row.shortwppm, row.short, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'long',
                    title: translate('sections.reports.catcher.blocking.long'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'long'));
                        return buildPercentCell(row.longwppm, row.long, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
            ];
        } else if (type === 'pitchtype') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                {
                    id: 'fb',
                    title: translate('baseball.pitches.abbr-fastball'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, 'fb'));
                        return buildPercentCell(row.fbwppm, row.fb, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'cb',
                    title: translate('baseball.pitches.abbr-curveball'),
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, 'cb'));
                        return buildPercentCell(row.cbwppm, row.cb, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'slcut',
                    title: `${translate('baseball.pitches.abbr-slider')}/${translate('baseball.pitches.abbr-cutter')}`,
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, 'slcut'));
                        return buildPercentCell(row.slcutwppm, row.slcut, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'chspl',
                    title: `${translate('baseball.pitches.abbr-changeup')}/${translate('baseball.pitches.abbr-splitter')}`,
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, 'chspl'));
                        return buildPercentCell(row.chsplwppm, row.chspl, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
            ];
        } else if (type === 'situation') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                {
                    id: '2k',
                    title: '2K',
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'twok'));
                        return buildPercentCell(row.twokwppm, row.twok, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'r1',
                    title: 'R1',
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'r1'));
                        return buildPercentCell(row.r1wppm, row.r1, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'r2',
                    title: 'R2',
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'r2'));
                        return buildPercentCell(row.r2wppm, row.r2, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'r3',
                    title: 'R3',
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'r3'));
                        return buildPercentCell(row.r3wppm, row.r3, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'r13',
                    title: 'R13',
                    transform: (row) => {
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, null, 'r13'));
                        return buildPercentCell(row.r13wppm, row.r13, 1, true, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
            ];
        } else if (type === 'short') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                {
                    id: 'glove',
                    title: translate('sections.reports.catcher.blocking.glove-side'),
                    transform: (row) => {
                        const pitchtype = pitchTypes.indexOf(row.pitchtype) > -1 ? row.pitchtype : null;
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, pitchtype, 'gloveside', 'short'));
                        return buildPercentCell(row.glovesidewppm, row.gloveside, true, 1, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'middle',
                    title: translate('sections.reports.catcher.blocking.middle'),
                    transform: (row) => {
                        const pitchtype = pitchTypes.indexOf(row.pitchtype) > -1 ? row.pitchtype : null;
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, pitchtype, 'middle', 'short'));
                        return buildPercentCell(row.middlewppm, row.middle, true, 1, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'arm',
                    title: translate('sections.reports.catcher.blocking.arm-side'),
                    transform: (row) => {
                        const pitchtype = pitchTypes.indexOf(row.pitchtype) > -1 ? row.pitchtype : null;
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, pitchtype, 'armside', 'short'));
                        return buildPercentCell(row.armsidewppm, row.armside, true, 1, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
            ];
        } else if (type === 'long') {
            columns = [
                { id: 'label', title: null, transform: (row) => (<StyledStrong>{row.label}</StyledStrong>), type: colType.NODE },
                {
                    id: 'glove',
                    title: translate('sections.reports.catcher.blocking.glove-side'),
                    transform: (row) => {
                        const pitchtype = pitchTypes.indexOf(row.pitchtype) > -1 ? row.pitchtype : null;
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, pitchtype, 'gloveside', 'long'));
                        return buildPercentCell(row.glovesidewppm, row.gloveside, true, 1, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'middle',
                    title: translate('sections.reports.catcher.blocking.middle'),
                    transform: (row) => {
                        const pitchtype = pitchTypes.indexOf(row.pitchtype) > -1 ? row.pitchtype : null;
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, pitchtype, 'middle', 'long'));
                        return buildPercentCell(row.middlewppm, row.middle, true, 1, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
                {
                    id: 'arm',
                    title: translate('sections.reports.catcher.blocking.arm-side'),
                    transform: (row) => {
                        const pitchtype = pitchTypes.indexOf(row.pitchtype) > -1 ? row.pitchtype : null;
                        const setPlaylistFn = () => onClick(getCategoryVideo(user, pitches, pitchtype, 'armside', 'long'));
                        return buildPercentCell(row.armsidewppm, row.armside, true, 1, setPlaylistFn);
                    },
                    type: colType.NODE,
                },
            ];
        }
        return columns;
    };
    const columns = getColumns(tableType);
    if (superheaderName) {
        const superheader = (
            <StyledSuperHeaderRow>
                <StyledSuperHeaderCell colSpan={columns.length}>{superheaderName}</StyledSuperHeaderCell>
            </StyledSuperHeaderRow>
        );
        return <DataTable superheader={superheader} columns={columns} rows={rows} />;
    }

    return (
        <DataTable columns={columns} rows={rows} />
    );
};

BlockingReportTable.propTypes = {
    tableType: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    superheaderName: PropTypes.string,
    pitches: PropTypes.arrayOf(PropTypes.object),
    onClick: PropTypes.func,
};

BlockingReportTable.defaultProps = {
    superheaderName: null,
    pitches: [],
    onClick: undefined,
};

export default BlockingReportTable;
